import React from "react";
import { useNavigate } from "react-router-dom";
import productImage from "../../shared/images/no-image.jpg";
import "./ProductCard.css";
import { useDispatch } from "react-redux";
import { addItemToCart } from "../../redux/Slices/Cart";
import { openCartModal } from "../../redux/Slices/CartModal";
import { productStock, formatMoney } from "../../utils/cartPricing";

const ProductCard = ({ data }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleCardClick = () => {
    navigate(`/product/${data?._id}`);
  };

  const handleAddToCart = (e) => {
    e.stopPropagation();
    const availableStock =
      typeof data?.stock === "number"
        ? data.stock > 0 && !data.isOutOfStock
          ? data.stock
          : 0
        : getLatestStockQuantity(data.stock?.stocks);

    // Create product object to add to cart
    const productToAdd = {
      productId: data._id,
      sku: data.sku,
      isBigBuyProduct: !!data.bigbuyId,
      name: data.name,
      size: "default",
      price: hasActiveDiscount()
        ? getCurrentPrice()
        : parseFloat(data.finalPrice || data.price) || 0,
      basePrice: getCurrentPrice(),
      originalPrice: getCurrentPrice(),
      quantity: 1,
      imageUrl: data.images?.length > 0 ? data.images[0] : productImage,
      discount: hasActiveDiscount() ? getDiscountPercentage() : null,
      quantityDiscount: ((data.quantityDiscount || data.quantity_discount) &&
                        (data.quantityDiscount || data.quantity_discount).quantity != null &&
                        ((data.quantityDiscount || data.quantity_discount).percentage != null || data.quantity_discount?.discount != null))
        ? {
            quantity: (data.quantityDiscount || data.quantity_discount).quantity,
            percentage:
              (data.quantityDiscount || data.quantity_discount).percentage ??
              data.quantity_discount?.discount,
          }
        : null,
    };

    if (productStock(data) <= 0) return;
    dispatch(
      addItemToCart({
        ...productToAdd,
        stock: availableStock,
      })
    );
    dispatch(openCartModal());
  };

  // Helper function to check if product has discount
  const hasActiveDiscount = () => {
    if (!data?.discount) return false;

    // Check if discount has price amount and is within date range
    const hasDiscountPrice = data.discount.price && data.discount.price > 0;

    // Check if discount is within valid date range
    const currentDate = new Date();
    const startDate = data.discount.start_date
      ? new Date(data.discount.start_date)
      : null;
    const endDate = data.discount.end_date
      ? new Date(data.discount.end_date)
      : null;

    const isWithinDateRange =
      (!startDate || currentDate >= startDate) &&
      (!endDate || currentDate <= endDate);

    return hasDiscountPrice && isWithinDateRange;
  };

  // Helper function to get original price before discount (same as ProductInformation.jsx)
  const getOriginalPrice = () => {
    if (hasActiveDiscount()) {
      // Calculate original price: finalPrice + discount amount
      return (
        parseFloat(data.finalPrice || 0) + parseFloat(data.discount.price || 0)
      );
    }
    return parseFloat(data.finalPrice || data.price || 0);
  };

  // Helper function to get current/discounted price (same as ProductInformation.jsx)
  const getCurrentPrice = () => {
    if (hasActiveDiscount()) {
      // Current price is just the finalPrice when discount is active
      return parseFloat(data.finalPrice || 0);
    }
    return parseFloat(data.finalPrice || data.price || 0);
  };

  // Helper function to get discount percentage for display
  const getDiscountPercentage = () => {
    if (hasActiveDiscount()) {
      const originalPrice = getOriginalPrice();
      const currentPrice = getCurrentPrice();
      const discountAmount = originalPrice - currentPrice;

      if (originalPrice <= 0) return 0;
      return Math.round((discountAmount / originalPrice) * 100);
    }
    return 0;
  };

  const getLatestStockQuantity = (stocks) => {
    // Handle new format where stock is a simple number
    if (typeof data?.stock === "number") {
      return data.stock > 0 && !data.isOutOfStock ? data.stock : 0;
    }

    // Handle old format with stocks array
    if (!stocks || stocks.length === 0) return "No stock available";

    // Get the stock entry with the highest quantity
    const latestStock = stocks.reduce((prev, current) =>
      prev.quantity > current.quantity ? prev : current
    );

    return latestStock.quantity > 0 ? latestStock.quantity : "Out of Stock";
  };

  return (
    <div className="product-component-card-2" onClick={handleCardClick}>
      <div className="product-image-section-2 image-relative">
        {data?.bigbuyId ? (
          <img
            src={data?.images[0]}
            alt={data?.name || "Product"}
            className="product-component-image-2"
          />
        ) : (
          <img
            src={data?.images?.length > 0 ? data?.images[0] : productImage}
            alt={data?.name || "Product"}
            className="product-component-image-2"
          />
        )}
        <div className="btn-wrap-2 btn-absolute">
          <button type="button" className="btn-2" onClick={handleAddToCart} disabled={productStock(data) <= 0} aria-label={`Lägg ${data?.name} i varukorgen`}>
            {productStock(data) > 0 ? "+ Köp" : "Slut i lager"}
          </button>
        </div>
      </div>
      <div className="product-details-section-2">
        <div className="product-component-info-2">
          <h4 className="product-component-title-2">{data?.name}</h4>
        </div>
        <div className="product-component-pricing-2">
          {hasActiveDiscount() ? (
            <div className="price-with-discount">
              <p>
                <span
                  style={{
                    textDecoration: "line-through",
                    color: "#999",
                    fontSize: "14px",
                    margin: "0",
                  }}
                >
                  {" "}
                  {formatMoney(getOriginalPrice())}
                </span>{" "}
                <span
                  style={{ color: "#e74c3c", fontWeight: "bold", margin: "0" }}
                >
                  {" "}
                  {formatMoney(getCurrentPrice())}
                </span>
              </p>
            </div>
          ) : (
            <div>
              <p className="product-component-price-2">
                <span>
                  {formatMoney(data?.finalPrice ?? data?.price)}
                </span>
              </p>
              {/* Show quantity discount info if available */}
              {data?.quantity_discount && 
               data.quantity_discount.quantity != null && 
               data.quantity_discount.percentage != null && (
                <div style={{ marginTop: "4px" }}>
                  <span className="quantity-discount-badge">
                    Köp {data.quantity_discount.quantity}+ för{" "}
                    {data.quantity_discount.percentage}% rabatt!
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
